import React, { useEffect, useState, useContext } from 'react';
import { format } from 'date-fns';
import { requestTradingData } from 'api/socketService';
import { useWalletData } from 'context/WalletDataContext';
import { formatCurrency } from 'utils';
import { spotPerformanceByBase } from 'utils/tradePerformance';
import type { TradePerformance } from 'utils/tradePerformance';
import SocketContext from 'utils/hooks/useSocket';
import { CASH_TICKER, isCash } from 'utils/cash';

import {
  Wrap,
  TabStrip,
  TabBtn,
  ListWrap,
  EmptyState,
  Load,
  LoadWrap,
  TradeItem,
  TradeHead,
  PairName,
  TypePill,
  TradeBody,
  Row,
  L,
  V,
  TimeText,
  AssetItem,
  AssetHead,
  AssetName,
  AssetValue,
  AssetBody,
} from './styled';

type Trade = {
  id: number;
  trading_pair: string;
  amount: number;
  price: number;
  market_value: number;
  type: string;
  createdAt: string;
  updatedAt: string;
};

type TabKey = 'history' | 'performance';

const TradeHistoryMobile = (props: { tournamentDetails: any }) => {
  const [activeTab, setActiveTab] = useState<TabKey>('history');
  const { tournamentDetails } = props;
  const [tradingData, setTradingData] = useState<Trade[]>([]);
  const { walletData } = useWalletData();
  const { allCoinsValues } = useContext(SocketContext);

  const updateTrading = (data: any) => {
    if (data?.success) {
      setTradingData(data?.data?.trading ?? []);
    } else {
      console.error('Error fetching trading data:');
    }
  };

  const fetchTradingData = () => {
    // TB-RULE-DATA-NORMALIZE — same Strapi v5 flat-vs-nested issue as
    // TradeHistory.tsx; without this normalize tradeType reads undefined and
    // the request is silently skipped.
    const rawData: any = tournamentDetails?.data;
    const attributes = rawData?.attributes ?? rawData?.data ?? rawData;
    const tournamentId = rawData?.id ?? attributes?.id;
    const tradeType = attributes?.trade_type;
    if (tournamentId && tradeType) {
      requestTradingData(tournamentId, tradeType, updateTrading);
    }
  };

  const [pairTradesData, updatePairTradesData] = useState<Record<string, TradePerformance>>({});
  const [currentWalletData, updateCurrentWalletData] = useState<any[] | null>(null);

  if (walletData?.length) {
    if (currentWalletData?.length) {
      walletData.forEach((element: any, i: number) => {
        if (element?.amount !== currentWalletData[i]?.amount) {
          updateCurrentWalletData(walletData);
        }
      });
    } else {
      updateCurrentWalletData(walletData);
    }
  }

  useEffect(() => {
    fetchTradingData();
  });

  const tradingPairWithAmount = (walletData ?? []).filter(
    (obj: any) => obj.amount > 0 && !isCash(obj)
  );

  useEffect(() => {
    updatePairTradesData(spotPerformanceByBase(tradingData, allCoinsValues));
  }, [allCoinsValues, tradingData]);

  return (
    <Wrap>
      <TabStrip>
        <TabBtn
          type="button"
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          Trade History
        </TabBtn>
        <TabBtn
          type="button"
          className={activeTab === 'performance' ? 'active' : ''}
          onClick={() => setActiveTab('performance')}
        >
          Asset Performance
        </TabBtn>
      </TabStrip>

      {activeTab === 'history' && (
        <>
          {tradingData.length > 0 ? (
            <ListWrap>
              {tradingData.map(trade => {
                const isBuy = trade.type === 'buy';
                return (
                  <TradeItem key={trade.id}>
                    <TradeHead>
                      <PairName>{trade.trading_pair}</PairName>
                      <TypePill $tone={isBuy ? 'bull' : 'bear'}>
                        {isBuy ? 'Buy' : 'Sell'}
                      </TypePill>
                    </TradeHead>
                    <TradeBody>
                      <Row>
                        <L>Amount</L>
                        <V>{trade.amount.toFixed(8)}</V>
                      </Row>
                      <Row>
                        <L>Price</L>
                        <V>{formatCurrency(trade.market_value)}</V>
                      </Row>
                      <Row>
                        <L>Value</L>
                        <V>{formatCurrency(trade.amount * trade.market_value)} {CASH_TICKER}</V>
                      </Row>
                      <Row>
                        <L>Time</L>
                        <V className="muted">
                          <TimeText>
                            {format(new Date(trade.createdAt), 'dd MMM HH:mm')}
                          </TimeText>
                        </V>
                      </Row>
                    </TradeBody>
                  </TradeItem>
                );
              })}
            </ListWrap>
          ) : (
            <Load>
              <LoadWrap />
              Loading...
            </Load>
          )}
        </>
      )}

      {activeTab === 'performance' && (
        <ListWrap>
          {tradingPairWithAmount.length === 0 ? (
            <EmptyState>No assets held</EmptyState>
          ) : (
            tradingPairWithAmount.map((pair: any) => {
              const perf = pairTradesData[pair.ticker.short_name];
              const plPercent = perf?.plPercent;
              const plClass = plPercent !== undefined && plPercent >= 0 ? 'bull' : 'bear';

              return (
                <AssetItem key={pair.id}>
                  <AssetHead>
                    <AssetName>{pair.ticker.short_name}</AssetName>
                    <AssetValue>
                      {formatCurrency(perf?.currentValue ?? 0)} {CASH_TICKER}
                    </AssetValue>
                  </AssetHead>
                  <AssetBody>
                    <Row>
                      <L>Amount</L>
                      <V>
                        {(perf?.totalCoins ?? 0).toFixed(8)} {pair.ticker.short_name}
                      </V>
                    </Row>
                    <Row>
                      <L>Avg price</L>
                      <V>{formatCurrency((perf?.avgPrice ?? 0).toFixed(2))} {CASH_TICKER}</V>
                    </Row>
                    <Row>
                      <L>P/L {CASH_TICKER}</L>
                      <V className={plClass}>
                        {formatCurrency(perf?.plUSDT ?? 0)} {CASH_TICKER}
                      </V>
                    </Row>
                    <Row>
                      <L>P/L %</L>
                      <V className={plClass}>
                        {(perf?.plPercent ?? 0).toFixed(2)}%
                      </V>
                    </Row>
                  </AssetBody>
                </AssetItem>
              );
            })
          )}
        </ListWrap>
      )}
    </Wrap>
  );
};

export default TradeHistoryMobile;
